import { PUBLIC_AUTH_CALLBACK_PATH } from '../../shared/publicationAccess.js';
import { normalizeAccountPrivacyAuthDestination } from '../../shared/privacy/userDataExportDeepLink.js';
import {
  clearContextualAuthIntentV2,
  loadContextualAuthIntentV2,
} from '@/components/layout/contextualPanelAuthIntent.js';

export const PUBLIC_AUTH_FALLBACK_PATH = '/';
export const PUBLIC_AUTH_DESTINATION_STORAGE_KEY = 'gameplaysync.publicAuthDestination.v1';
export const DEFAULT_PUBLIC_AUTH_DESTINATION_TTL_MS = 30 * 60 * 1000;

const PUBLIC_AUTH_DESTINATION_SCHEMA_VERSION = 1;
const hasStorageApi = (storage) =>
  storage &&
  typeof storage.getItem === 'function' &&
  typeof storage.setItem === 'function' &&
  typeof storage.removeItem === 'function';

const resolveSessionStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return hasStorageApi(window.sessionStorage) ? window.sessionStorage : null;
  } catch {
    return null;
  }
};

const removeStoredDestination = (storage) => {
  try {
    storage.removeItem(PUBLIC_AUTH_DESTINATION_STORAGE_KEY);
    return true;
  } catch {
    return false;
  }
};

export function normalizePublicAuthDestination(value) {
  return normalizeAccountPrivacyAuthDestination(value);
}

export function clearPublicAuthDestination({ storage = resolveSessionStorage() } = {}) {
  if (!hasStorageApi(storage)) {
    return false;
  }
  return removeStoredDestination(storage);
}

export function savePublicAuthDestination(
  destination,
  {
    storage = resolveSessionStorage(),
    now = Date.now(),
    ttlMs = DEFAULT_PUBLIC_AUTH_DESTINATION_TTL_MS,
  } = {},
) {
  if (!hasStorageApi(storage)) {
    return false;
  }

  const path = normalizePublicAuthDestination(destination);
  const createdAt = Number(now);
  const requestedTtl = Number(ttlMs);
  if (!path || !Number.isFinite(createdAt) || createdAt <= 0) {
    removeStoredDestination(storage);
    return false;
  }

  const safeTtl = Number.isFinite(requestedTtl) && requestedTtl > 0
    ? Math.min(requestedTtl, DEFAULT_PUBLIC_AUTH_DESTINATION_TTL_MS)
    : DEFAULT_PUBLIC_AUTH_DESTINATION_TTL_MS;
  const record = {
    version: PUBLIC_AUTH_DESTINATION_SCHEMA_VERSION,
    path,
    createdAt,
    expiresAt: createdAt + safeTtl,
  };

  clearContextualAuthIntentV2({ storage });
  try {
    storage.setItem(PUBLIC_AUTH_DESTINATION_STORAGE_KEY, JSON.stringify(record));
    return true;
  } catch {
    removeStoredDestination(storage);
    return false;
  }
}

export function loadPublicAuthDestination({
  storage = resolveSessionStorage(),
  now = Date.now(),
} = {}) {
  if (!hasStorageApi(storage)) {
    return null;
  }

  let rawValue;
  try {
    rawValue = storage.getItem(PUBLIC_AUTH_DESTINATION_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!rawValue) {
    return null;
  }

  try {
    const record = JSON.parse(rawValue);
    const currentTime = Number(now);
    const path = normalizePublicAuthDestination(record?.path);
    const keys = record && typeof record === 'object' ? Object.keys(record).sort() : [];
    const expectedKeys = ['createdAt', 'expiresAt', 'path', 'version'];
    const hasExactShape = keys.length === expectedKeys.length &&
      keys.every((key, index) => key === expectedKeys[index]);
    const createdAt = Number(record?.createdAt);
    const expiresAt = Number(record?.expiresAt);
    const ttlMs = expiresAt - createdAt;
    if (
      hasExactShape &&
      record.version === PUBLIC_AUTH_DESTINATION_SCHEMA_VERSION &&
      path &&
      Number.isFinite(currentTime) &&
      Number.isFinite(createdAt) &&
      Number.isFinite(expiresAt) &&
      createdAt > 0 &&
      createdAt <= currentTime &&
      ttlMs > 0 &&
      ttlMs <= DEFAULT_PUBLIC_AUTH_DESTINATION_TTL_MS &&
      expiresAt > currentTime
    ) {
      return path;
    }
  } catch {
    // Invalid records are removed below.
  }

  removeStoredDestination(storage);
  return null;
}

export function consumePublicAuthDestination({
  preferredDestination,
  storage = resolveSessionStorage(),
  now = Date.now(),
} = {}) {
  const preferredPath = normalizePublicAuthDestination(preferredDestination);
  const storedPath = loadPublicAuthDestination({ storage, now });
  const directPath = preferredPath || storedPath;
  if (directPath) {
    return clearPublicAuthDestination({ storage }) ? directPath : PUBLIC_AUTH_FALLBACK_PATH;
  }

  return resolvePublicAuthDestination({ storage, now });
}

const resolveBrowserOrigin = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.location?.origin || null;
};

export function resolvePublicAuthDestination({
  preferredDestination,
  storage,
  now = Date.now(),
} = {}) {
  const directPath = normalizePublicAuthDestination(preferredDestination) ||
    loadPublicAuthDestination({
      ...(storage === undefined ? {} : { storage }),
      now,
    });
  if (directPath) {
    return directPath;
  }

  const intent = loadContextualAuthIntentV2({
    ...(storage === undefined ? {} : { storage }),
    now,
  });

  return intent?.returnPath || PUBLIC_AUTH_FALLBACK_PATH;
}

export function buildPublicAuthCallbackUrl({ origin = resolveBrowserOrigin() } = {}) {
  if (!origin) {
    return null;
  }

  try {
    const parsedOrigin = new URL(origin);
    if (!['http:', 'https:'].includes(parsedOrigin.protocol)) {
      return null;
    }
    return new URL(PUBLIC_AUTH_CALLBACK_PATH, `${parsedOrigin.origin}/`).toString();
  } catch {
    return null;
  }
}

export { PUBLIC_AUTH_CALLBACK_PATH };
